'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useMemo, useRef, type PropsWithChildren } from 'react';
import type { PanelTypes } from '@/components/panels/panelRegistry';
import { WorkspaceContext } from '@/contexts/workspaceContext';
import { addEntry, closeEntry, markEnteredEntry, removeEntry } from './snackbarEntries';
import { PanelSize, type SidePanelSize, type DashboardAiEntryStore, type DialogEntry, type DialogOptions, type DialogStore, type SnackbarEntry, type SnackbarHandle, type SnackbarOptions, type SnackbarStore, type SnackbarVariant, type SidePanelStore } from './workspaceContextTypes';
const DEFAULT_SNACKBAR_AUTO_HIDE_DURATION = 3000;
const getSnackbarAutoHideDuration = (options?: SnackbarOptions) => options?.autoHideDuration === undefined ? DEFAULT_SNACKBAR_AUTO_HIDE_DURATION : options.autoHideDuration;
export const WorkspaceProvider = t0 => {
  const $ = _c(27);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [dialogStack, setDialogStack] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (id, content, options) => {
      setDialogStack(stack => [...stack, {
        isLoading: false,
        ...options,
        id,
        content
      }]);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const openDialog = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setDialogStack(_temp);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const backDialog = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setDialogStack([]);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const closeAllDialogs = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (id_0, content_0, options_0) => {
      setDialogStack(stack_1 => [...stack_1.slice(0, -1), {
        isLoading: false,
        ...options_0,
        id: id_0,
        content: content_0
      }]);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const replaceDialog = t5;
  let t6;
  if ($[5] !== dialogStack) {
    t6 = id_1 => {
      if (id_1) {
        return dialogStack.some(d => d.id === id_1);
      }
      return dialogStack.length > 0;
    };
    $[5] = dialogStack;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const isDialogOpen = t6;
  let t7;
  if ($[7] !== dialogStack || $[8] !== isDialogOpen) {
    t7 = {
      stack: dialogStack,
      open: openDialog,
      back: backDialog,
      closeAll: closeAllDialogs,
      replace: replaceDialog,
      isOpen: isDialogOpen
    };
    $[7] = dialogStack;
    $[8] = isDialogOpen;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const sharedDialog = t7;
  const [rightPanel, t8] = useState({
    isOpen: false,
    isAbsolute: false,
    isResizable: false,
    isPersistent: false,
    Component: undefined,
    panelSize: PanelSize.Large,
    setSize: panelSize => setRightPanel(state => ({
      ...state,
      panelSize
    })),
    setResizedWidth: resizedWidth => setRightPanel(state_0 => ({
      ...state_0,
      resizedWidth
    })),
    setIsOpen: (isOpen, panelSize_0, isAbsolute, isResizable, isPersistent) => setRightPanel(state_1 => ({
      ...state_1,
      isOpen,
      isAbsolute: isAbsolute ?? false,
      isResizable: isOpen ? isResizable ?? false : state_1.isResizable,
      isPersistent: isPersistent ?? false,
      panelSize: isOpen ? panelSize_0 ?? PanelSize.Large : state_1.panelSize
    })),
    setComponent: Component => setRightPanel(state_2 => ({
      ...state_2,
      Component
    }))
  });
  const setRightPanel = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const [snackbarEntries, setSnackbarEntries] = useState(t9);
  const nextSnackbarId = useRef(0);
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = (variant, message, options_1) => {
      nextSnackbarId.current = nextSnackbarId.current + 1;
      const id_2 = nextSnackbarId.current;
      const hide = () => setSnackbarEntries(e => closeEntry(e, id_2));
      const remove = () => setSnackbarEntries(e_0 => removeEntry(e_0, id_2));
      const markEntered = () => setSnackbarEntries(e_1 => markEnteredEntry(e_1, id_2));
      setSnackbarEntries(e_2 => addEntry(e_2, {
        id: id_2,
        isOpen: true,
        hasEntered: false,
        markEntered,
        message,
        variant,
        action: options_1?.action,
        autoHideDuration: getSnackbarAutoHideDuration(options_1),
        hide,
        remove
      }));
      return {
        hide
      };
    };
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const showSnackbar = t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = (message_0, options_2) => showSnackbar("default", message_0, options_2);
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const showMessage = t11;
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = (message_1, options_3) => showSnackbar("error", message_1, options_3);
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const showErrorMessage = t12;
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = (message_2, options_4) => showSnackbar("default", message_2, options_4);
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const showWarningMessage = t13;
  let t14;
  if ($[15] !== snackbarEntries) {
    t14 = {
      entries: snackbarEntries,
      showMessage,
      showErrorMessage,
      showWarningMessage
    };
    $[15] = snackbarEntries;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  const snackbar = t14;
  const [dashboardAiEntryVisible, setDashboardAiEntryVisible] = useState(false);
  let t15;
  if ($[17] !== dashboardAiEntryVisible) {
    t15 = {
      isVisible: dashboardAiEntryVisible,
      setIsVisible: setDashboardAiEntryVisible
    };
    $[17] = dashboardAiEntryVisible;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  const dashboardAiEntry = t15;
  let t16;
  if ($[19] !== dashboardAiEntry || $[20] !== rightPanel || $[21] !== sharedDialog || $[22] !== snackbar) {
    t16 = {
      rightPanel,
      snackbar,
      sharedDialog,
      dashboardAiEntry
    };
    $[19] = dashboardAiEntry;
    $[20] = rightPanel;
    $[21] = sharedDialog;
    $[22] = snackbar;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== children || $[25] !== t16) {
    t17 = <WorkspaceContext.Provider value={t16}>{children}</WorkspaceContext.Provider>;
    $[24] = children;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  return t17;
};
function _temp(stack_0) {
  return stack_0.slice(0, -1);
}